const Events = require('../events/index');
const Core = require('../core');
const { getUserObject } = require('../services/userService');
const { getDestinationObject } = require('../services/destinationService');
const { getSearchTermsObject } = require('../services/searchTermsService');
const { getDealObject } = require('../services/dealService');
const { getPointsObject } = require('../services/pointsService');
const { getBookingObject } = require('../services/bookingService');
const { getCouponObject } = require('../services/couponService');

const { DealUtils } = require('../../entities');

const _popupExposureCount = {};

function _getAndIncrementPopupCount(search_id) {
    const key = search_id || '__unknown__';
    _popupExposureCount[key] = (_popupExposureCount[key] || 0) + 1;
    return _popupExposureCount[key];
}

function _buildRateCheckMetadata({ popup_type, price_changed, old_price, new_price, deadline_changed, board_changed, deal, search_id, hotel }) {
    const price_change_direction = price_changed ? (new_price > old_price ? 'increase' : 'decrease') : null;
    const changes_presented = [];
    if (price_changed) {
        changes_presented.push(price_change_direction === 'increase' ? 'price_increase' : 'price_decrease');
    }
    if (deadline_changed) {
        changes_presented.push('policy_change');
    }
    if (board_changed) {
        changes_presented.push('board_change');
    }

    return {
        popup_type,
        changes_presented,
        price_change_direction,
        original_price: price_changed ? (old_price ?? null) : null,
        new_price: price_changed ? (new_price ?? null) : null,
        price_delta: price_changed ? Math.abs(new_price - old_price) : null,
        currency: deal?.currency ?? null,
        policy_changed: !!deadline_changed,
        board_changed: !!board_changed,
        search_id: search_id ?? null,
        hotel_id: hotel?.id ?? hotel?._id ?? deal?.hotel?.id ?? deal?.hotel?._id ?? deal?.details?.hotel_id ?? null,
        hotel_name: hotel?.name ?? hotel?.title ?? deal?.hotel?.name ?? deal?.hotel?.title ?? deal?.details?.hotel_name ?? null,
        room_type: deal?.room ?? null,
        supplier_id: deal ? (DealUtils.dealSupplierExtractor(deal).supplier ?? null) : null,
    };
}

function fireResults(count, wait_time_ml) {
    const metadata = {
        count,
        wait_time_ml
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_FIRE, metadata);
}

function fireFilteredResults(count, wait_time_ml) {
    const metadata = {
        count,
        wait_time_ml
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_HOTEL_FILTERS, metadata);
}

function hotelsResults(count, wait_time_ml, context = {}) {
    const { user, destination, search, hotels } = context;
    const metadata = {
        count,
        wait_time_ml,
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
        ...(Array.isArray(hotels) && hotels.length > 0 && { hotels }),
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_HOTELS, metadata);
}

function resultsFetchingTime(load_time) {
    const metadata = {
        load_time,
    }
    
    Core.trackEvent(Events.RESULTS.RESULTS_FETCHING_TIME, metadata);
}

function searchWithoutResults(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCH_WITHOUT_RESULTS, metadata);
}

function searchedHotelWithoutDeals(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCHED_HOTEL_WITHOUT_DEALS, metadata);
}

function searchWithoutResultsHotel(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCH_WITHOUT_RESULTS_HOTEL, metadata);
}

function rateUnavailable(room_name, price, hotel_name) {
    const metadata = {
        room_name,
        price,
        hotel_name,
    };

    Core.trackEvent(Events.RESULTS.RATE_UNAVAILABLE, metadata);
}

function bookingCreated({
                            user,
                            destination,
                            hotel,
                            search,
                            deal,
                            payment_method,
                            charge_currency,
                            points,
                            geolocationSearch,
                            distance,
                            attempt_count,
                            session_attempt_count,
                            hotel_attempt_count,
                            booking_id,
                            status,
                            triggered_by_rate_check,
                            rate_check_change_types,
                            points_toggle_state_at_booking,
                            coupon,
                            bnpl,
                        }) {
    const points_redeemed = (points && points.show && points.user_chosen_points) || 0;
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination, hotel),
        ...getSearchTermsObject(search),
        ...getDealObject(deal),
        ...getPointsObject(points),
        ...getCouponObject(coupon, charge_currency || (deal && deal.currency)),
        payment_method,
        charge_currency,
        geolocationSearch,
        distance,
        attempt_count,
        session_attempt_count,
        hotel_attempt_count,
        booking_id,
        status,
        triggered_by_rate_check: triggered_by_rate_check ?? false,
        rate_check_change_types: rate_check_change_types ?? null,
        points_redeemed,
        ...(points_toggle_state_at_booking && { points_toggle_state_at_booking }),
        ...(bnpl || {}),
    };

    Core.trackEvent(Events.RESERVATION.BOOKING_CREATED, metadata);
}

function reservationCanceled(user, reservation_id, destination, booking) {
    const metadata = {
        ...getUserObject(user),
        reservation_id,
        ...getDestinationObject(destination),
        ...getBookingObject(booking),
    };

    Core.trackEvent(Events.RESERVATION.RESERVATION_CANCELED, metadata);
}

function reservationCanceledFailed(user, reservation_id, hotel_name) {
    const metadata = {
        ...getUserObject(user),
        reservation_id,
        hotel_name
    };

    Core.trackEvent(Events.RESERVATION.RESERVATION_CANCELED_FAILED, metadata);
}

function reservationFailed({
                               user,
                               destination,
                               deal,
                               payment_method,
                               error,
                               hotel,
                               status,
                               failure_reason,
                               attempt_count,
                               session_attempt_count,
                               hotel_attempt_count,
                           }) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination, hotel),
        ...getDealObject(deal),
        payment_method,
        error,
        status,
        failure_reason,
        attempt_count,
        session_attempt_count,
        hotel_attempt_count,
    };
    Core.trackEvent(Events.RESERVATION.RESERVATION_FAILED_TO_BOOK, metadata);
}

function httpError(request, respond, endpoint) {
    const metadata = {
        request,
        respond,
        endpoint
    };

    Core.trackEvent(Events.HTTP.HTTP_ERROR, metadata);
}

function roomRateCheckPopupShown({ popup_type, price_changed, old_price, new_price, deadline_changed, board_changed, deal, search_id, hotel }) {
    const popup_exposure_count_in_search = _getAndIncrementPopupCount(search_id);
    const metadata = {
        ..._buildRateCheckMetadata({ popup_type, price_changed, old_price, new_price, deadline_changed, board_changed, deal, search_id, hotel }),
        popup_exposure_count_in_search,
    };
    Core.trackEvent(Events.RESERVATION.ROOM_RATE_CHECK_POPUP_SHOWN, metadata);
    return popup_exposure_count_in_search;
}

function roomRateCheckPopupAction({ action, popup_type, price_changed, old_price, new_price, deadline_changed, board_changed, deal, search_id, popup_exposure_count_in_search, hotel }) {
    const metadata = {
        action,
        ..._buildRateCheckMetadata({ popup_type, price_changed, old_price, new_price, deadline_changed, board_changed, deal, search_id, hotel }),
        popup_exposure_count_in_search: popup_exposure_count_in_search ?? null,
    };
    Core.trackEvent(Events.RESERVATION.ROOM_RATE_CHECK_POPUP_ACTION, metadata);
}

module.exports = {
    fireResults,
    fireFilteredResults,
    hotelsResults,
    resultsFetchingTime,
    searchWithoutResults,
    searchWithoutResultsHotel,
    rateUnavailable,
    bookingCreated,
    reservationCanceled,
    reservationCanceledFailed,
    reservationFailed,
    httpError,
    searchedHotelWithoutDeals,
    roomRateCheckPopupShown,
    roomRateCheckPopupAction,
};
